/**
 * NewWorkflowModal — used by the Workflow Engine landing page to start
 * a brand-new workflow definition without cloning an existing version.
 *
 * Two starting points:
 *   1. `blank` — one empty Stage with one empty Step, ready to design.
 *   2. `template` — copies the stages from a chosen Program Template's
 *      canonical workflow, regenerates IDs so it lives as a brand-new
 *      slug (no version-collision with the parent flagship workflow).
 *
 * On submit, POSTs to /admin/wfe/definitions and redirects into the
 * designer.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, X } from "lucide-react";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function newId(prefix) {
  return `${prefix}${Math.random().toString(16).slice(2, 14)}`;
}

// Walks a stages array and regenerates every id (stage/step/trigger/action/condition/outcome).
// Keeps `key` values (used by conditions for branching) intact so links between steps survive.
function regenerateIds(stages) {
  return (stages || []).map((stage) => ({
    ...stage,
    id: newId("stg_"),
    steps: (stage.steps || []).map((step) => ({
      ...step,
      id: newId("stp_"),
      triggers: (step.triggers || []).map((t) => ({ ...t, id: newId("trg_") })),
      actions: (step.actions || []).map((a) => ({ ...a, id: newId("act_") })),
      conditions: (step.conditions || []).map((c) => ({ ...c, id: newId("cnd_") })),
      outcomes: (step.outcomes || []).map((o) => ({ ...o, id: newId("out_") })),
    })),
  }));
}

const BLANK_STAGES = [
  {
    id: newId("stg_"),
    key: "start",
    name: "Start",
    order: 0,
    steps: [
      {
        id: newId("stp_"),
        key: "first_step",
        name: "First step",
        order: 0,
        triggers: [],
        actions: [],
        conditions: [],
        outcomes: [],
      },
    ],
  },
];

export default function NewWorkflowModal({ open, onClose, templates }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [startingPoint, setStartingPoint] = useState("blank"); // 'blank' | 'template'
  const [templateSlug, setTemplateSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugManuallyEdited(false);
      setDescription("");
      setStartingPoint("blank");
      setTemplateSlug(templates?.[0]?.slug || "");
      setSubmitting(false);
    }
  }, [open, templates]);

  // Auto-slug from name unless user has edited slug manually.
  useEffect(() => {
    if (!slugManuallyEdited) setSlug(slugify(name));
  }, [name, slugManuallyEdited]);

  const canSubmit = useMemo(() => {
    if (!name.trim() || !slug.trim()) return false;
    if (startingPoint === "template" && !templateSlug) return false;
    return !submitting;
  }, [name, slug, startingPoint, templateSlug, submitting]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let stages = BLANK_STAGES;
      if (startingPoint === "template") {
        // Fetch the chosen template's canonical workflow stages, then
        // re-id them so we don't collide with the source workflow.
        const r = await api.get(`/admin/wfe/program-templates/${templateSlug}`);
        const sourceStages = r.data?.canonical_definition?.stages || [];
        if (sourceStages.length === 0) {
          toast.error("Selected template has no stages to copy.");
          setSubmitting(false);
          return;
        }
        stages = regenerateIds(sourceStages);
      }

      const payload = {
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() || null,
        version: 1,
        state: "draft",
        stages,
      };
      const r = await api.post("/admin/wfe/definitions", payload);
      toast.success(`Created draft v${r.data.version} of "${r.data.name}"`);
      onClose?.();
      navigate(`/admin/workflow-engine/designer/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-workflow-modal"
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600">
              <Sparkles className="w-3 h-3" /> New workflow
            </div>
            <h2 className="mt-1 font-display text-xl font-bold text-slate-900">
              Create a workflow from scratch
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              You&apos;ll start in <strong>draft</strong> state. Nothing is live until you deploy.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="new-workflow-close"
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Name <span className="text-rose-500">*</span>
            </label>
            <input
              data-testid="new-workflow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Patient acquisition funnel"
              autoFocus
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Slug <span className="text-rose-500">*</span>
            </label>
            <input
              data-testid="new-workflow-slug"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugManuallyEdited(true);
              }}
              placeholder="patient-acquisition-funnel"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Stable identifier — used in triggers, conditions, and analytics. Lowercase, hyphens only.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Description
            </label>
            <textarea
              data-testid="new-workflow-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional — what this workflow orchestrates."
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Starting point
            </label>
            <div className="mt-1.5 space-y-1.5">
              <label
                data-testid="new-workflow-start-blank"
                className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer transition-colors ${
                  startingPoint === "blank" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  name="starting_point"
                  value="blank"
                  checked={startingPoint === "blank"}
                  onChange={() => setStartingPoint("blank")}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Blank workflow</div>
                  <div className="text-xs text-slate-500">One empty stage with one empty step. Build everything yourself.</div>
                </div>
              </label>

              <label
                data-testid="new-workflow-start-template"
                className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer transition-colors ${
                  startingPoint === "template" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  name="starting_point"
                  value="template"
                  checked={startingPoint === "template"}
                  onChange={() => setStartingPoint("template")}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">Copy structure from a flagship template</div>
                  <div className="text-xs text-slate-500">
                    Inherit stages + steps from an existing program. Lives as a brand-new workflow you fully own.
                  </div>
                  {startingPoint === "template" && (
                    <select
                      data-testid="new-workflow-template-select"
                      value={templateSlug}
                      onChange={(e) => setTemplateSlug(e.target.value)}
                      className="mt-2 w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs bg-white"
                    >
                      {(templates || []).map((t) => (
                        <option key={t.slug} value={t.slug}>
                          {t.name} ({t.category})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="new-workflow-cancel"
            className="text-sm font-semibold text-slate-700 px-3 py-2 rounded-full hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="new-workflow-submit"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-3.5 h-3.5" /> {submitting ? "Creating…" : "Create & open in designer"}
          </button>
        </div>
      </form>
    </div>
  );
}
